import React from 'react'
import PropTypes from 'prop-types'
import {Blocks} from '@builder.io/sdk-react'
import {
    CarouselContent,
    CarouselItem,
    slidesVisibleValuesMobile,
    slidesVisibleValuesTablet,
    slidesVisibleValuesDesktop
} from '@/components/ui/carousel'
import {defaultSlide} from './defaultConfig'

export const CustomCarouselContent = (props) => {
    const {
        slides,
        slidesVisible,
        showPeek,
        showPeekTablet,
        showPeekMobile,
        slidesVisibleTablet,
        slidesVisibleMobile,
        slidePadding,
        slidePaddingTablet,
        slidePaddingMobile,
        builderBlock,
        attributes
    } = props
    return (
        <CarouselContent {...attributes} data-testid="carouselContent-CarouselContent">
            {slides?.map((slide, index) => (
                <CarouselItem
                    key={index}
                    slidesVisible={slidesVisible}
                    slidesVisibleTablet={slidesVisibleTablet}
                    slidesVisibleMobile={slidesVisibleMobile}
                    slidePadding={slidePadding}
                    slidePaddingTablet={slidePaddingTablet}
                    showPeek={showPeek}
                    showPeekTablet={showPeekTablet}
                    showPeekMobile={showPeekMobile}
                    slidePaddingMobile={slidePaddingMobile}
                    data-testid="carouselContent-CarouselItem"
                >
                    <Blocks
                        path={`component.options.slides.${index}.content`}
                        blocks={[slide]}
                        parent={builderBlock?.id}
                        data-testid="carouselContent-Blocks"
                    />
                </CarouselItem>
            ))}
        </CarouselContent>
    )
}

CustomCarouselContent.propTypes = {
    attributes: PropTypes.object,
    builderBlock: PropTypes.object,
    slides: PropTypes.arrayOf(PropTypes.object.isRequired),
    slidesVisible: PropTypes.string,
    slidesVisibleTablet: PropTypes.string,
    slidesVisibleMobile: PropTypes.string,
    slidePadding: PropTypes.string,
    slidePaddingTablet: PropTypes.string,
    slidePaddingMobile: PropTypes.string,
    showPeek: PropTypes.bool,
    showPeekTablet: PropTypes.bool,
    showPeekMobile: PropTypes.bool
}

export const CarouselContentDefinition = {
    component: CustomCarouselContent,
    name: 'v1: CarouselContent',
    canHaveChildren: true,
    noWrap: true,
    shouldReceiveBuilderProps: {
        builderBlock: true
    },
    defaultStyles: {
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    inputs: [
        {
            name: 'slides',
            type: 'list',
            subFields: [
                {
                    name: 'content',
                    type: 'uiBlocks',
                    defaultValue: Array(3).fill(defaultSlide)
                }
            ],
            defaultValue: Array(6).fill(defaultSlide)
        },
        {
            name: 'slidesVisible',
            friendlyName: 'Number of Slides Visible (Desktop)',
            type: 'string',
            enum: slidesVisibleValuesDesktop,
            defaultValue: '1',
            helperText: 'Number of slides visible on desktop (lg and above)'
        },
        {
            name: 'showPeek',
            type: 'boolean',
            default: false,
            helperText:
                'Select true if you want padding-right removed and for the next card to peek out'
        },
        {
            name: 'slidesVisibleTablet',
            friendlyName: 'Number of Slides Visible (Tablet)',
            type: 'string',
            enum: slidesVisibleValuesTablet,
            helperText:
                'Number of slides visible on tablet devices (md breakpoint). Leave unset to inherit desktop value.'
        },
        {
            name: 'showPeekTablet',
            type: 'boolean',
            default: false,
            helperText:
                'Select true if you want padding-right removed and for the next card to peek out on tablet'
        },
        {
            name: 'slidesVisibleMobile',
            friendlyName: 'Number of Slides Visible (Mobile)',
            type: 'string',
            enum: slidesVisibleValuesMobile,
            defaultValue: '1',
            helperText: 'Number of slides visible on mobile devices'
        },
        {
            name: 'showPeekMobile',
            type: 'boolean',
            default: true,
            helperText:
                'Select true if you want padding-right removed and for the next card to peek out'
        },
        {
            name: 'slidePadding',
            friendlyName: 'Padding Between Slides (Desktop)',
            type: 'string',
            enum: Array.from({length: 7}, (v, i) => i.toString()),
            defaultValue: '2',
            helperText: 'Padding between slides on desktop'
        },
        {
            name: 'slidePaddingTablet',
            friendlyName: 'Padding Between Slides (Tablet)',
            type: 'string',
            enum: Array.from({length: 7}, (v, i) => i.toString()),
            helperText: 'Padding between slides on tablet. Leave unset to inherit desktop value.'
        },
        {
            name: 'slidePaddingMobile',
            friendlyName: 'Padding Between Slides (Mobile)',
            type: 'string',
            enum: Array.from({length: 7}, (v, i) => i.toString()),
            defaultValue: '2',
            helperText: 'Padding between slides on mobile'
        }
    ]
}
